/**
 * A tab opened before a deploy keeps running the old bundle. Its lazy pages point at chunk files
 * the new build replaced, so the first visit to a page it has not loaded yet 404s — typically
 * `/login` after the session expires overnight. One reload fetches the new `index.html` (served
 * no-store) and with it the current chunk names.
 *
 * The reload is rate-limited per tab so a chunk that is genuinely missing on the server cannot
 * loop the page; the second failure inside the window falls through to the error panel.
 */

const RELOAD_KEY = 'hopebot.staleBuildReloadAt';
const RELOAD_GAP_MS = 10_000;

// Chrome, Firefox, Safari, and Vite's own CSS preload helper word it differently.
const CHUNK_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

export function isChunkLoadError(error: unknown): boolean {
  return error instanceof Error && CHUNK_ERROR.test(error.message);
}

/** True when this tab has not reloaded for a stale build in the last few seconds. */
export function mayReloadForNewBuild(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY)) || 0;
    return Date.now() - last >= RELOAD_GAP_MS;
  } catch {
    // No storage, no loop guard — leave it to the person.
    return false;
  }
}

/** Reloads the page once per window. Returns false when the guard held it back. */
export function reloadForNewBuild(): boolean {
  if (!mayReloadForNewBuild()) return false;
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}
